import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiAlertTriangle,
  FiUser,
  FiMessageSquare,
  FiFileText,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiMinusCircle,
  FiAlertCircle,
  FiEdit3,
} from "react-icons/fi";

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
};

const ReportCard = ({ report, onAction, loading }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [moderatorNotes, setModeratorNotes] = useState("");

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getReasonConfig = (reason) => {
    const configs = {
      spam: {
        bgColor: "rgba(239, 68, 68, 0.1)",
        borderColor: "rgba(239, 68, 68, 0.3)",
        textColor: "#dc2626",
      },
      harassment: {
        bgColor: "rgba(236, 72, 153, 0.1)",
        borderColor: "rgba(236, 72, 153, 0.3)",
        textColor: "#db2777",
      },
      inappropriate: {
        bgColor: "rgba(245, 158, 11, 0.1)",
        borderColor: "rgba(245, 158, 11, 0.3)",
        textColor: "#d97706",
      },
      misinformation: {
        bgColor: "rgba(249, 115, 22, 0.1)",
        borderColor: "rgba(249, 115, 22, 0.3)",
        textColor: "#ea580c",
      },
      other: {
        bgColor: "rgba(100, 116, 139, 0.1)",
        borderColor: "rgba(100, 116, 139, 0.3)",
        textColor: "#64748b",
      },
    };
    return configs[reason] || configs.other;
  };

  const handleActionClick = (action) => {
    setSelectedAction(action);
    setShowConfirm(true);
  };

  const handleConfirmAction = async () => {
    if (selectedAction && onAction) {
      await onAction(report._id, selectedAction, moderatorNotes);
      setShowConfirm(false);
      setSelectedAction(null);
      setModeratorNotes("");
    }
  };

  const handleCancelAction = () => {
    setShowConfirm(false);
    setSelectedAction(null);
    setModeratorNotes("");
  };

  const renderTargetPreview = () => {
    // NEW API structure: uses contentAvailable, contentPreview, reportedContent
    const content = report.reportedContent;
    const isAvailable = report.contentAvailable !== false && content;
    
    if (!isAvailable) {
      return (
        <div className="collab-comm-mod-target-preview unavailable">
          <div className="collab-comm-mod-target-icon">
            <FiAlertTriangle size={16} />
          </div>
          <div className="collab-comm-mod-target-content">
            <span className="collab-comm-mod-target-label">Content</span>
            <p>Content no longer available</p>
          </div>
        </div>
      );
    }
    
    // Thread report
    if (report.reportType === "thread" || content.title) {
      return (
        <div className="collab-comm-mod-target-preview thread">
          <div className="collab-comm-mod-target-icon">
            <FiFileText size={16} />
          </div>
          <div className="collab-comm-mod-target-content">
            <span className="collab-comm-mod-target-label">
              Thread by {content.author?.name || 'Unknown Author'}
            </span>
            <p>{report.contentPreview || content.title}</p>
          </div>
        </div>
      );
    }
    
    // Reply report
    if (report.reportType === "reply" || content.content) {
      return (
        <div className="collab-comm-mod-target-preview reply">
          <div className="collab-comm-mod-target-icon">
            <FiMessageSquare size={16} />
          </div>
          <div className="collab-comm-mod-target-content">
            <span className="collab-comm-mod-target-label">
              Reply by {content.author?.name || 'Unknown Author'}
            </span>
            <p>
              {report.contentPreview || content.content?.substring(0, 120)}
              {(content.content?.length || 0) > 120 ? "..." : ""}
            </p>
          </div>
        </div>
      );
    }
    
    // Generic fallback with preview
    return (
      <div className="collab-comm-mod-target-preview">
        <div className="collab-comm-mod-target-icon">
          <FiFileText size={16} />
        </div>
        <div className="collab-comm-mod-target-content">
          <span className="collab-comm-mod-target-label">
            {report.reportType || 'Content'}
          </span>
          <p>{report.contentPreview || 'Reported content'}</p>
        </div>
      </div>
    );
  };

  const reasonConfig = getReasonConfig(report.reason);

  return (
    <motion.div
      className="collab-comm-mod-report-card"
      variants={itemVariants}
      layout
    >
      <div className="collab-comm-mod-report-header">
        <div className="collab-comm-mod-reporter">
          <div className="collab-comm-mod-reporter-avatar">
            <FiUser size={14} />
          </div>
          <span>
            Reported by{" "}
            <strong>
              {report.reporter?.name ||
                report.reporter?.username ||
                report.reportedBy?.name ||
                report.reportedBy?.username ||
                "Unknown"}
            </strong>
          </span>
        </div>
        <div
          className="collab-comm-mod-reason-badge"
          style={{
            backgroundColor: reasonConfig.bgColor,
            borderColor: reasonConfig.borderColor,
            color: reasonConfig.textColor,
          }}
        >
          <FiAlertCircle size={12} />
          {report.reason || report.reportType}
        </div>
      </div>

      <div className="collab-comm-mod-report-body">
        {renderTargetPreview()}

        {report.description && (
          <div className="collab-comm-mod-report-description">
            <div className="collab-comm-mod-description-header">
              <FiEdit3 size={14} />
              <span>Reporter's Description</span>
            </div>
            <p>{report.description}</p>
          </div>
        )}

        <div className="collab-comm-mod-report-meta">
          <div className="collab-comm-mod-timestamp">
            <FiClock size={14} />
            <span>{formatDate(report.createdAt)}</span>
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {!showConfirm ? (
          <motion.div
            key="actions"
            className="collab-comm-mod-report-actions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <button
              className="collab-comm-mod-action-btn approve"
              onClick={() => handleActionClick("none")}
              disabled={loading}
            >
              <FiCheckCircle size={16} />
              <span>Approve</span>
            </button>
            <button
              className="collab-comm-mod-action-btn remove"
              onClick={() => handleActionClick("content_removed")}
              disabled={loading}
            >
              <FiXCircle size={16} />
              <span>Remove</span>
            </button>
            <button
              className="collab-comm-mod-action-btn dismiss"
              onClick={() => handleActionClick("dismiss")}
              disabled={loading}
            >
              <FiMinusCircle size={16} />
              <span>Dismiss</span>
            </button>
          </motion.div>
        ) : (
          <motion.div
            key="confirm"
            className="collab-comm-mod-confirm-panel"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="collab-comm-mod-confirm-header">
              <FiAlertTriangle size={18} />
              <strong>Confirm {selectedAction?.toUpperCase()} action?</strong>
            </div>
            <textarea
              placeholder="Add moderator notes (optional)..."
              value={moderatorNotes}
              onChange={(e) => setModeratorNotes(e.target.value)}
              className="collab-comm-mod-notes-input"
              rows={3}
            />
            <div className="collab-comm-mod-confirm-buttons">
              <button
                className="collab-comm-mod-confirm-btn"
                onClick={handleConfirmAction}
                disabled={loading}
              >
                {loading ? (
                  <React.Fragment>
                    <div className="collab-comm-btn-spinner" />
                    Processing...
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <FiCheckCircle size={16} />
                    Confirm
                  </React.Fragment>
                )}
              </button>
              <button
                className="collab-comm-mod-cancel-btn"
                onClick={handleCancelAction}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ReportCard;
